import { routesMap } from 'app/Router';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxGalleryImg } from 'components/RevxGalleryImg';
import { RevxInputNumber } from 'components/RevxInputNumber';
import {
  Paragraph,
  SectionHeading,
  UnderlineText,
} from 'components/RevxTypography';
import { CartLineItem } from 'domains/cartDomain';
import { GALLERY_SKU_ENUM, GallerySku } from 'domains/gallerySkuDomain';
import {
  useCanvaDesignSize,
  useCanvaMatchView,
  useCanvaPublishDraft,
  useGallerySkuFilter,
  useUser,
} from 'hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ALBERTSONS_ORGS, JOB_MAX_QUANTITY } from 'utils/constants';
import { isBuyer } from 'utils/rbac';
import { Select, Skeleton } from 'antd';
import './CompanyCard.less';
import { CompanyCardInfiniteList } from './CompanyCardInfiniteList';
import { CompanyModal } from './CompanyModal';
import { CompanyRequestProductCard } from './CompanyRequestProductCard';
import { SkuMatchChips } from './SkuMatchChips';

export const CompanyCard = (props: {
  onTotalChange?: (total: number, items: GallerySku[]) => void;
  /**
   * Restricts the list to one product category. Used by the Canva category picker, where the
   * buyer has opened a category out of the full gallery and has to say which of its products the
   * parked design goes on.
   */
  galleryLabelId?: number;
}) => {
  const [items] = useState<GallerySku[]>([]);
  const [open, setOpen] = useState(false);
  const [sku, setSku] = useState<GallerySku>();
  return (
    <>
      <SkuItems
        items={items}
        onTotalChange={props.onTotalChange}
        galleryLabelId={props.galleryLabelId}
        sku={sku}
        setSku={setSku}
        open={open}
        setOpen={setOpen}
      />
      <CompanyModal open={open} setOpen={setOpen} sku={sku} setSku={setSku} />
    </>
  );
};

interface ISkuItemsProps {
  items?: GallerySku[];
  onTotalChange?: (total: number, items: GallerySku[]) => void;
  galleryLabelId?: number;
  sku?: GallerySku;
  setSku: (sku?: GallerySku) => void;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

const SkuItems = (props: ISkuItemsProps) => {
  // const { items = [] } = props;
  const dispatch = useAppDispatch();
  const history = useHistory();
  const selectedSkuType = useAppSelector(
    (state) => state.companyGallery.selectedSkuType
  );
  const filter = useGallerySkuFilter();
  // Design parked by the Canva app: every pick here finishes that publish instead.
  const { isCanvaPublish, publishWithSku } = useCanvaPublishDraft();
  // Ranks the whole result by how well each SKU fits the parked design; empty on a normal visit.
  const { designSize, loading: designSizeLoading } = useCanvaDesignSize();
  // The match landing view: products that can hold the design, gathered from every category.
  const { isMatchView, isTaggedPick } = useCanvaMatchView();
  const setCurrentSkuType = async ({
    id,
    preConfigured,
    defaultQty,
  }: GallerySku) => {
    if (isCanvaPublish) {
      await publishWithSku(Number(id));
      return;
    }
    if (preConfigured) {
      return;
    }
    try {
      const configItem = await dispatch.jobConfig.startJobConfig({
        skuId: id,
        quantity: defaultQty,
        isExpert: false,
      });
      history.push(routesMap.jobConfig.path + '/' + configItem.id);
      dispatch.companyGallery.setSelectedSkuType(null);
    } catch (e) {
      throw new Error('error');
    }
  };

  const itemRender = ({ items }: { items?: GallerySku[] | undefined }) => {
    if (!items || !items.length) {
      // Nothing to show, which is exactly when asking for a product is worth offering.
      return (
        <div>
          <CompanyRequestProductCard />
        </div>
      );
    }
    return (
      <>
        {items.map((item, index) => {
          const {
            id,
            name,
            images,
            description,
            defaultQty,
            unitPriceConfigured,
            quantities,
          } = item;
          return (
            <div>
              {item.preConfigured && !isCanvaPublish ? (
                <CompanyListPreConfigItem
                  {...props}
                  item={{
                    id,
                    name,
                    image: images[0],
                    defaultQty,
                    description,
                    unitPriceConfigured,
                    quantities,
                  }}
                  sku={item}
                  onClick={async (quantity, directlyAddToCart) => {
                    try {
                      return await dispatch.jobConfig.directlyAddToCart({
                        skuId: Number(id),
                        quantity: quantity,
                        isExpert: false,
                        directlyAddToCart: directlyAddToCart,
                      });
                    } catch {
                      throw new Error('error');
                    }
                  }}
                />
              ) : (
                <CompanyListItem
                  {...props}
                  item={{ ...item, id, image: images[0], name, description }}
                  sku={item}
                  onClick={async () => {
                    try {
                      return await setCurrentSkuType(item);
                    } catch {
                      throw new Error('error');
                    }
                  }}
                />
              )}
            </div>
          );
        })}
        <div>
          <CompanyRequestProductCard />
        </div>
      </>
    );
  };

  // The infinite list fetches page 1 as soon as it mounts, so mounting it before the parked
  // design's size has arrived asks for the catalogue unranked -- and nothing re-fetches page 1
  // when the size lands, since only a category change resets the list. Holding the mount is what
  // guarantees the first request carries the size, rather than relying on the page-level remount
  // to correct a request that was already wrong.
  // Not for a tagged hand-off: it does not use the design size, so waiting on it only delays
  // a list that would be identical either way.
  if (designSizeLoading && !isTaggedPick) {
    return (
      <div className="company-card-grid">
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className="company-card-grid">
      <CompanyCardInfiniteList
        loadMore={async (page) => {
          page = page || 1;
          const listData = await dispatch.gallery.getCompanySkusByLabel({
            // Never scoped to a company category on a Canva visit, in either state: the buyer
            // is matched against every active SKU in the org, and -1 ("any company category")
            // would drop every SKU that has no company label at all -- which, on the Canva org,
            // is all of them.
            companyLabelId: isCanvaPublish
              ? undefined
              : Number(selectedSkuType?.skuLabel?.id) ||
                Number(selectedSkuType?.id) ||
                -1,
            // The Canva category picker asks for one product category; every other caller
            // leaves it unset and gets the whole gallery.
            galleryLabelId: props.galleryLabelId,
            page,
            // A Canva visit with no named products is matched against every active SKU in the
            // org, so the company-gallery filter must not narrow the field. A hand-off that did
            // name them is the opposite case: those tags are the whole point of the visit.
            filter: isCanvaPublish && !isTaggedPick ? undefined : filter,
            // Likewise the design size. With the products already named there is nothing for it
            // to decide, and ranking or badging them by fit would answer a question nobody asked.
            designSize: isTaggedPick ? undefined : designSize,
            matchedOnly: isMatchView,
          });
          return listData;
        }}
        render={itemRender}
        selectedSkuType={selectedSkuType}
        onTotalChange={props.onTotalChange}
        // Inside the Canva shell (the matches, or one category's products) the result is one
        // short row far more often than not, and the reserved height would strand the
        // "Browse all products" link below it a screen down.
        minHeight={isMatchView || props.galleryLabelId != null ? 0 : undefined}
      />
    </div>
  );
};

interface ICompanyListPreConfigItemProps {
  item: {
    id: number;
    name: string;
    image: string;
    description: string;
    skuId?: string;
    defaultQty?: number;
    unitPriceConfigured?: boolean;
    quantities?: number[];
  };
  onClick: (x: number, directlyAddToCart: boolean) => Promise<CartLineItem>;
  sku: GallerySku;
  setSku: (sku?: GallerySku) => void;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

const CompanyListPreConfigItem = (props: ICompanyListPreConfigItemProps) => {
  const { item, onClick, setOpen, setSku, sku } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const quantityOptions = item.quantities || [];
  /* the api only returns quantities when the buyer has to be limited to them, no matter whether they
     come from the unit price matrix or from the sku custom quantity drop-down */
  const shouldUseQuantitySelect = quantityOptions.length > 0;
  const [quantity, setQuantity] = useState(() => {
    const defaultQuantity = Number(item.defaultQty || 100);
    if (!shouldUseQuantitySelect) {
      return defaultQuantity;
    }
    return quantityOptions.includes(defaultQuantity)
      ? defaultQuantity
      : quantityOptions[0];
  });
  const [addToCartLoading, setAddToCartLoading] = useState<boolean>(false);
  const [comparisonLoading, setComparisonLoading] = useState<boolean>(false);
  // const history = useHistory();
  const user = useUser();
  return (
    <div className="gallery-item" key={`preConfig${item.id}${item.image}`}>
      <div
        className="gallery-item-img"
        onClick={() => {
          setSku?.({
            ...sku,
            defaultQty: quantity,
          });
          if (typeof setOpen === 'function') {
            setOpen(true);
          }
        }}
      >
        {item.image ? (
          <RevxGalleryImg src={item.image} />
        ) : (
          <span className="img"></span>
        )}
      </div>
      <SectionHeading
        className="gallery-item-type-name"
        ellipsis={{
          tooltip: item.name,
          rows: 3,
        }}
        onClick={() => {
          setSku({
            ...sku,
            defaultQty: quantity,
          });
          if (typeof setOpen === 'function') {
            setOpen(true);
          }
        }}
      >
        {item.name}
      </SectionHeading>
      {/* <div className="gallery-item-desc">{item.description || ' '}</div> */}
      <RevxFlexLayout style={{ marginBottom: '6px' }} justify="space-between">
        <RevxFlexLayout justify="justify-center">
          <label style={{ paddingRight: '6px', fontWeight: 500 }}>
            {t('common.quantityAbbrev')}:{' '}
          </label>
          {shouldUseQuantitySelect ? (
            <Select<number>
              style={{ height: '28px', width: '90px' }}
              value={quantity}
              disabled={sku?.qtyLocked}
              onChange={(value) => setQuantity(value)}
            >
              {quantityOptions.map((qty) => (
                <Select.Option key={qty} value={qty}>
                  {qty}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <RevxInputNumber
              style={{ height: '28px', maxWidth: '90px' }}
              isInteger
              min={1}
              max={JOB_MAX_QUANTITY}
              value={quantity}
              disabled={sku?.qtyLocked}
              onChange={(e) => setQuantity(e)}
            />
          )}
        </RevxFlexLayout>
        <RevxFlexLayout
          justify="justify-center"
          hidden={item.unitPriceConfigured}
        >
          <RevxButton
            type="link"
            style={{ textDecoration: 'underline' }}
            hidden={isBuyer(user)}
            loading={comparisonLoading}
            onClick={async () => {
              try {
                setComparisonLoading(true);
                dispatch.gallery.updateGalleryModalType(
                  GALLERY_SKU_ENUM.COMPARE
                );
                setSku({
                  ...sku,
                  defaultQty: quantity,
                });
                if (typeof setOpen === 'function') {
                  setOpen(true);
                }
              } catch {
              } finally {
                setComparisonLoading(false);
              }
            }}
          >
            <UnderlineText color="green">{t('common.compare')}</UnderlineText>
          </RevxButton>
        </RevxFlexLayout>
      </RevxFlexLayout>
      <RevxButton
        size="large"
        type="primary"
        loading={addToCartLoading}
        onClick={async () => {
          try {
            if (typeof onClick === 'function') {
              setAddToCartLoading(true);
              const cartItem = await onClick(quantity, true);
              // history.push(routesMap.shoppingCart.path);
              await dispatch.cart.getList();
              dispatch.cart.updateCartItem(cartItem);
              dispatch.workspace.reloadJobList();
            }
          } catch {
          } finally {
            setAddToCartLoading(false);
          }
        }}
      >
        {t('gallery.skuList.addToCart')}
      </RevxButton>
    </div>
  );
};

interface ICompanyListItemProps {
  item: {
    id: number;
    name: string;
    image: string;
    description: string;
  };
  onClick: () => Promise<any>;
  sku?: GallerySku;
  setSku?: (sku?: GallerySku) => void;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

const CompanyListItem = (props: ICompanyListItemProps) => {
  const { item, onClick } = props;
  const { t } = useTranslation();
  const [shopLoading, setShopLoading] = useState<boolean>(false);
  const orgId = useAppSelector((state) => state.app.orgId);

  return (
    <div className="gallery-item">
      <div>
        <div className="gallery-item-img" style={{ cursor: 'default' }}>
          {item.image ? (
            <RevxGalleryImg src={item.image} />
          ) : (
            <span className="img"></span>
          )}
        </div>
        <Paragraph
          className="gallery-item-type-name"
          style={{
            textAlign: 'center',
            cursor: 'default',
          }}
          ellipsis={{
            rows: 3,
            expandable: true,
            symbol: ' ',
            tooltip: item.name,
          }}
        >
          {item.name}
        </Paragraph>
        <SkuMatchChips sku={props.sku} />
      </div>
      <RevxButton
        size="large"
        type="primary"
        loading={shopLoading}
        onClick={async () => {
          try {
            if (onClick) {
              setShopLoading(true);
              await onClick();
            }
          } catch {
          } finally {
            setShopLoading(false);
          }
        }}
      >
        {ALBERTSONS_ORGS.includes(orgId)
          ? t('gallery.skuList.customize')
          : t('gallery.skuList.choose-options')}
      </RevxButton>
    </div>
  );
};
